import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Activity, Gauge, Info, Loader2, Play } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  getMeasuredOverhead,
  runPolicyOverheadSelfTest,
  type PolicyOverheadSelfTest,
} from '@/api/benchmark';

/**
 * Verify our latency claim, in the app, on your own account.
 *
 * This page replaces the benchmark script we used to publish for download.
 * That script imported private backend modules, so no reader could actually
 * run it. The rule now: verification lives here, behind the user's own login,
 * and the user runs it themselves.
 *
 * Two panels, deliberately separate, because they are two different numbers:
 * the measured overhead from your own retrievals is the figure we publish;
 * the self-test isolates policy evaluation, which is a small fraction of it.
 */

function Stat({
  label,
  value,
  unit = 'ms',
}: {
  label: string;
  value: number | null | undefined;
  unit?: string;
}) {
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="text-2xl font-bold text-foreground tabular-nums">
        {value === null || value === undefined ? '--' : `${value}${unit}`}
      </div>
      <div className="mt-1 text-xs font-medium text-muted-foreground">{label}</div>
    </div>
  );
}

function SelfTestResult({ result }: { result: PolicyOverheadSelfTest }) {
  return (
    <div className="mt-6 space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="p50 policy evaluation" value={result.p50_ms} />
        <Stat label="p95 policy evaluation" value={result.p95_ms} />
        <Stat label="p99 policy evaluation" value={result.p99_ms} />
      </div>

      <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
        <p>
          {result.iterations} measured iterations ({result.warmup_iterations} warmup, excluded),
          each evaluating {result.candidates_per_iteration} resources against{' '}
          {result.policies_evaluated}{' '}
          {result.policies_evaluated === 1 ? 'policy' : 'policies'} carrying{' '}
          {result.rules_evaluated} {result.rules_evaluated === 1 ? 'rule' : 'rules'}. The run
          allowed {result.candidates_allowed} and denied {result.candidates_denied}.
        </p>
        <p className="mt-2">
          {result.policy_source === 'organization'
            ? 'Evaluated against your own active policies.'
            : 'Your organization has no active policies yet, so this ran against a built-in 5-rule reference policy.'}
        </p>
      </div>

      <div className="rounded-lg border border-border p-4">
        <p className="text-sm font-semibold text-foreground">What this number excludes</p>
        <ul className="mt-2 space-y-1">
          {result.excluded.map((item) => (
            <li key={item} className="text-sm text-muted-foreground">
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function PolicyOverheadPage() {
  const [iterations] = useState(150);

  const measured = useQuery({
    queryKey: ['benchmark', 'measured-overhead'],
    queryFn: () => getMeasuredOverhead(30),
  });

  const selfTest = useMutation({
    mutationFn: () => runPolicyOverheadSelfTest(iterations),
  });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Performance Self-Test</h1>
        <p className="mt-2 max-w-3xl text-muted-foreground">
          Measure what Gateco&apos;s policy layer costs, against this running service and your own
          account. Neither measurement on this page calls a vector database or an embedding
          provider, so neither one consumes your retrieval quota.
        </p>
      </div>

      <Card className="p-6">
        <div className="flex items-start gap-3">
          <Gauge className="mt-0.5 h-5 w-5 shrink-0 text-primary-600" />
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-foreground">
              Your measured overhead
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Every retrieval records how long the whole call took and how much of that was your
              vector database. The difference is the policy layer. This is the same quantity we
              publish, recomputed from your own traffic.
            </p>
          </div>
        </div>

        {measured.isLoading && (
          <p className="mt-6 text-sm text-muted-foreground">Reading your retrieval records...</p>
        )}

        {measured.isError && (
          <p className="mt-6 text-sm text-destructive">
            Could not read your retrieval records. Try again shortly.
          </p>
        )}

        {measured.data && measured.data.sample_count === 0 && (
          <div className="mt-6 flex gap-3 rounded-lg border border-border bg-muted/30 p-4">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No retrievals recorded in the last {measured.data.window_days} days yet, so there is
              nothing to summarize. Run a retrieval and this fills in. The self-test below works
              straight away and needs no traffic.
            </p>
          </div>
        )}

        {measured.data && measured.data.sample_count > 0 && (
          <div className="mt-6 space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <Stat label="p50 policy overhead" value={measured.data.p50_ms} />
              <Stat label="p95 policy overhead" value={measured.data.p95_ms} />
              <Stat label="p99 policy overhead" value={measured.data.p99_ms} />
            </div>
            <p className="text-sm text-muted-foreground">
              Over {measured.data.sample_count}{' '}
              {measured.data.sample_count === 1 ? 'retrieval' : 'retrievals'} in the last{' '}
              {measured.data.window_days} days. For context, your median total call was{' '}
              {measured.data.total_p50_ms}ms, of which your connector accounted for{' '}
              {measured.data.connector_p50_ms}ms.
            </p>
          </div>
        )}
      </Card>

      <Card className="p-6">
        <div className="flex items-start gap-3">
          <Activity className="mt-0.5 h-5 w-5 shrink-0 text-primary-600" />
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-foreground">Policy engine self-test</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Runs the same policy evaluation function every live retrieval calls, over a fixed
              30-resource set, using your own active policies. This isolates policy evaluation
              from the database reads and audit writes that the figure above includes, so expect
              it to be a small fraction of that number.
            </p>
          </div>
        </div>

        <div className="mt-6">
          <Button onClick={() => selfTest.mutate()} disabled={selfTest.isPending}>
            {selfTest.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Running {iterations} iterations...
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Run the self-test
              </>
            )}
          </Button>
        </div>

        {selfTest.isError && (
          <p className="mt-4 text-sm text-destructive">
            The self-test could not complete. If you have run it several times in the last minute,
            wait a moment and try again.
          </p>
        )}

        {selfTest.data && <SelfTestResult result={selfTest.data} />}
      </Card>
    </div>
  );
}
